import { DOCUMENT } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';

import { SWIPER_INTERACTION_EMITTER, SwiperInteractionEvent } from '@planone/shared/data-access-swiper';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';

import SwiperCore, { EffectFade, Scrollbar, SwiperOptions } from 'swiper';

import { SwiperZoomModalComponent } from '../swiper-zoom-modal/swiper-zoom-modal.component';

SwiperCore.use([Scrollbar, EffectFade]);

@Component({
  selector: 'planone-swiper',
  templateUrl: './swiper.component.html',
  styleUrls: ['./swiper.component.scss'],
})
export class SwiperComponent implements OnInit, AfterViewChecked, OnChanges {
  @ContentChildren('slide') slides!: QueryList<TemplateRef<never>> | null;
  @ContentChildren('title') titles!: QueryList<TemplateRef<never>> | null;
  @ViewChild('swiperSlider') swiper!: unknown;
  @Input() swiperOptions: SwiperOptions = {};
  @Input() showSlideCount = true;
  @Input() showSlideNav = true;
  @Input() showSlideControl = false;
  @Input() enableZoom = true;
  @Input() moveToBack = false;
  @Input() forceAspectRatio = true;
  @Input() desktopVariant = 'default'; // Available variants: 'default', 'list', 'preview'
  @Input() mobileVariant = 'default'; // Available variants: 'default', 'cards'
  @Output() zoomChange: EventEmitter<boolean>;
  @Output() interaction: EventEmitter<SwiperInteractionEvent>;
  screenSize$: Observable<string | undefined>;
  slideCount?: number;
  slideVisibleCount?: number;
  slideActive?: number;
  zoomed = false;
  zoomedIndex?: number;
  expandable = false;
  expanded = false;
  limit = 0;
  slidesActive: TemplateRef<never>[] = [];
  fullScreenZoom = false;

  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(SWIPER_INTERACTION_EMITTER) private swiperInteractionEmitter: BehaviorSubject<SwiperInteractionEvent>,
    public dialog: MatDialog,
    private zone: NgZone,
    private screensizeFacade: DataAccessScreensizeFacade,
    private changeDetector: ChangeDetectorRef
  ) {
    this.slideCount = 0;
    this.slideActive = 1;
    this.screenSize$ = screensizeFacade.size$;
    this.zoomChange = new EventEmitter<boolean>();
    this.interaction = new EventEmitter<SwiperInteractionEvent>();

    this.screenSize$.pipe(takeUntil(this.unsubscribe)).subscribe((screenSize) => {
      const slideCount = this.slides?.length ?? 0;

      this.expandable = (slideCount > 8 && screenSize === 'desktop') || (slideCount > 6 && screenSize === 'tablet');
      this.limit = screenSize === 'desktop' || screenSize === 'laptop' ? 8 : screenSize === 'tablet' ? 6 : 0;

      this.expanded ? this.showMore() : this.showLess();
      this.fullScreenZoom = screenSize === 'phone';
    });
  }

  ngOnInit() {
    if (this.swiperOptions.slidesPerView && this.swiperOptions.slidesPerView !== 'auto') {
      this.slideVisibleCount = this.swiperOptions.slidesPerView;
    }

    this.sliderPreviewDrag();
  }

  ngOnChanges() {
    const slidesPerView =
      typeof this.swiperOptions.slidesPerView === 'string' ? 1 : this.swiperOptions.slidesPerView ?? 1;
    const slides = this.slides?.length ?? 0;

    this.swiperOptions.slidesPerView =
      slides > 0 ? (slides > slidesPerView ? slidesPerView : Math.floor(slidesPerView)) : slidesPerView;
    this.swiperOptions.slidesPerGroup = Math.floor(this.swiperOptions.slidesPerView);
  }

  ngAfterViewChecked() {
    if (this.slidesActive.length === 0) {
      this.showLess();
      this.changeDetector.detectChanges();
    }
  }

  showLess() {
    if (this.slides) {
      const activeItems: TemplateRef<never>[] = [];

      this.slides.forEach((slide: TemplateRef<never>, index: number) => {
        if (index < this.limit) {
          activeItems.push(slide);
        } else if (!this.expandable) {
          this.expandable = true;
        }
      });

      this.slidesActive = activeItems;
      this.expanded = false;
    }
  }

  showMore() {
    if (this.slides) {
      const activeItems: TemplateRef<never>[] = [];

      this.slides.forEach((slide: TemplateRef<never>) => {
        activeItems.push(slide);
      });

      this.slidesActive = activeItems;
      this.expanded = true;
    }
  }

  sliderNext() {
    (<any>this.swiper)?.swiperRef.slideNext(100);
  }

  sliderPrevious() {
    (<any>this.swiper)?.swiperRef.slidePrev(100);
  }

  sliderSlide(index: number) {
    (<any>this.swiper)?.swiperRef.slideTo(index);
  }

  sliderChange(swiper: any) {
    const activeIndex = swiper[0].activeIndex;

    this.zone.run(() => {
      this.slideActive = activeIndex + 1;

      this.swiperInteractionEmitter.next({
        type: 'SWIPE_TO',
        index: activeIndex,
      });
    });
  }

  sliderInit() {
    this.slideCount = this.slides?.length ?? 0;
  }

  toggleZoom(zoomedIndex: number | undefined = undefined) {
    this.zoomedIndex = zoomedIndex;
    this.enableZoom ? (this.zoomed = !this.zoomed) : false;

    if (this.desktopVariant === 'list') {
      this.slideActive = (this.zoomedIndex ?? 0) + 1;
    }

    if (this.enableZoom) {
      this.openZoomModal();
    }
  }

  openZoomModal() {
    this.dialog.open(SwiperZoomModalComponent, {
      width: this.fullScreenZoom ? '100vw' : '80vw',
      maxWidth: this.fullScreenZoom ? '100vw' : '80vw',
      maxHeight: 'calc(100vh - 48px)',
      data: {
        slides: this.slides,
        titles: this.titles,
        slideCount: this.slideCount,
        slideActive: this.slideActive,
        swiperOptions: this.swiperOptions,
        parent: this.swiper,
      },
    });

    this.interaction.emit({
      type: 'OPEN',
      index: (this.slideActive ?? 1) - 1,
    });
  }

  sliderPreviewDrag() {
    const slider = this.document.getElementById('sliderMetaUnzoomedPreviews');

    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    const sliderPreviews = this.document.getElementsByClassName('slider-previews-item');

    Array.prototype.forEach.call(sliderPreviews, (parent) => {
      Array.prototype.forEach.call(parent.children, (child) => {
        child.setAttribute('draggable', false);
      });
    });

    slider?.addEventListener('mousedown', (e) => {
      isDown = true;
      slider.classList.add('active');
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider?.addEventListener('touchstart', (e) => {
      isDown = true;
      slider.classList.add('active');
      startX = e.touches[0].pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider?.addEventListener('mouseleave', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('touchcancel', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('mouseup', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('touchend', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 3;
      slider.scrollLeft = scrollLeft - walk;
    });

    slider?.addEventListener('touchmove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.touches[0].pageX - slider.offsetLeft;
      const walk = (x - startX) * 3;
      slider.scrollLeft = scrollLeft - walk;
    });
  }

  identifyByIndex(index: number): string {
    return `${index}`;
  }
}
